import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom-v5-compat";
import { getCarRoutes } from "@b2bportal/core-cars";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import { Funnel } from "@b2bportal/core-types";
import {
  FLIGHTS_PATH,
  getPackageRoutes,
  ACTIVITIES_PATH,
  useDeviceType,
} from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  useGetCurrentFunnel,
  HOTELS_PATH,
  useIsHomepage,
} from "@overrides/utilities";
import { Button, ButtonTypeEnum, Icon } from "@tenant/ui";
import clsx from "clsx";
import styles from "./FunnelNavigation.module.scss";
import { useFunnelEnabled } from "@/modules/experiments/src";
import { BonusEarnPill } from "@/modules/wallet";
import { TenantIconNameEnum } from "@/types/tenantIcons";

// Matches both confirmation patterns:
const STANDALONE_CONFIRMATION_PATH_REGEX =
  /^\/(flights|hotels|cars|packages|activities)\/confirmation\/[^/]+\/?$/;
const CHECKOUT_BOOK_PATH_REGEX =
  /^\/(flights|stays|vacation-rentals|cars|activities)\/book\/?$/;

interface TabInfo {
  selected: boolean;
  path: string;
  icon: IconNameEnum;
  activeIcon: IconNameEnum;
  title: string;
  enabled: boolean;
  badge?: ReactNode;
}

export const FunnelNavigation = () => {
  const location = useLocation();
  const isHomepage = useIsHomepage();

  // Only show on homepage and confirmation pages
  const isConfirmationPage = useMemo(
    () =>
      STANDALONE_CONFIRMATION_PATH_REGEX.test(location.pathname) ||
      (CHECKOUT_BOOK_PATH_REGEX.test(location.pathname) &&
        new URLSearchParams(location.search).get("checkoutState") ===
          "bookingConfirmation"),
    [location.pathname, location.search]
  );

  const [block, cn] = useModuleBEM(styles, "FunnelNavigation");
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(true);

  const currentFunnel = useGetCurrentFunnel() ?? Funnel.Lodging;
  const { t } = useI18nContext();
  const { isDesktopAndUp } = useDeviceType();
  const {
    isFlightsEnabled,
    isStaysEnabled,
    isCarsEnabled,
    isActivitiesEnabled,
    isPackagesEnabled,
  } = useFunnelEnabled();

  const { BASE_ROUTE: CAR_BASE_ROUTE } = getCarRoutes();
  const { BASE_ROUTE: PACKAGES_BASE_ROUTE } = getPackageRoutes();
  const allTabs = [
    {
      selected: currentFunnel === Funnel.Air,
      path: FLIGHTS_PATH,
      icon: IconNameEnum.flights,
      activeIcon: IconNameEnum.flights__active,
      title: t("core-flights.funnelName"),
      enabled: isFlightsEnabled,
    },
    {
      selected: currentFunnel === Funnel.Lodging,
      path: HOTELS_PATH,
      icon: IconNameEnum.stays,
      activeIcon: IconNameEnum.stays__active,
      title: t("core-hotels.funnelName"),
      enabled: isStaysEnabled,
      badge: <BonusEarnPill />,
    },
    {
      selected: currentFunnel === Funnel.Ground,
      path: CAR_BASE_ROUTE,
      icon: IconNameEnum.cars,
      activeIcon: IconNameEnum.cars__active,
      title: t("core-cars.funnelName"),
      enabled: isCarsEnabled,
    },
    {
      selected: currentFunnel === Funnel.Package,
      path: PACKAGES_BASE_ROUTE,
      icon: IconNameEnum.packages,
      activeIcon: IconNameEnum.packages__active,
      title: t("core-packages.funnelName"),
      enabled: isPackagesEnabled,
    },
    {
      selected: currentFunnel === Funnel.Activities,
      path: ACTIVITIES_PATH,
      icon: IconNameEnum.activities,
      activeIcon: IconNameEnum.activities__active,
      title: t("core-activities.funnelName"),
      enabled: isActivitiesEnabled,
    },
  ];

  const tabs = allTabs.filter((tab) => tab.enabled);
  const checkButtons = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowLeft(scrollLeft > 0);
      setShowRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  useEffect(() => {
    checkButtons();
    window.addEventListener("resize", checkButtons);
    return () => window.removeEventListener("resize", checkButtons);
  }, []);

  const scroll = (offset: number) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: offset,
        behavior: "smooth",
      });
    }
  };
  const navLink = ({
    selected,
    path,
    title,
    icon,
    activeIcon,
    badge,
  }: TabInfo) => (
    <NavLink
      key={path}
      className={cn("funnel-navigation-button", {
        selected,
      })}
      to={path}
    >
      <Icon
        className={cn("funnel-icon")}
        iconName={selected ? activeIcon : icon}
        useCurrentColor={false}
      />
      <span
        className={cn("funnel-navigation-title", {
          selected,
        })}
      >
        {title}
      </span>
      {badge}
    </NavLink>
  );

  if (!isHomepage && !isConfirmationPage) {
    return null;
  }

  return (
    <div className={clsx(block)}>
      {!isDesktopAndUp && showLeft ? (
        <Button
          className={cn("tab-button", ["back"])}
          onClick={() => scroll(-150)}
          buttonType={ButtonTypeEnum.Tertiary}
          aria-label={t("home.funnelNavigation.scrollLeft")}
        >
          <Icon iconName={TenantIconNameEnum.chevron} />
        </Button>
      ) : null}
      <div
        className={cn("funnel-navigation")}
        onScroll={checkButtons}
        ref={scrollRef}
      >
        {tabs.map((tab) => navLink(tab))}
      </div>
      {!isDesktopAndUp && showRight ? (
        <Button
          className={cn("tab-button", ["forward"])}
          onClick={() => scroll(150)}
          buttonType={ButtonTypeEnum.Tertiary}
          aria-label={t("home.funnelNavigation.scrollRight")}
        >
          <Icon iconName={TenantIconNameEnum.chevron} />
        </Button>
      ) : null}
    </div>
  );
};
